import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, Download, Award, Trash2, FileText, FileSpreadsheet, Lock, Unlock, Send, ClipboardList, BarChart3, Search, Check, Pencil, Save, BookOpen, Paperclip } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import RichTextEditor from '../components/RichTextEditor';
import CoursePreinscriptions from '../components/CoursePreinscriptions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Estados de alumno disponibles
const ESTADOS_ALUMNO = [
  { value: 'PENDIENTE', label: 'Pendiente', color: 'bg-slate-100 text-slate-700' },
  { value: 'APTO', label: 'Apto', color: 'bg-green-100 text-green-700' },
  { value: 'NO APTO', label: 'No Apto', color: 'bg-red-100 text-red-700' },
  { value: 'NO ASISTE', label: 'No Asiste', color: 'bg-red-100 text-red-700' },
  { value: 'NO ASISTE JUSTIFICADO', label: 'No Asiste Justificado', color: 'bg-amber-100 text-amber-700' },
  { value: 'NO TERMINADO', label: 'No Terminado', color: 'bg-orange-100 text-orange-700' },
];

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [showCertDialog, setShowCertDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudentForCert, setSelectedStudentForCert] = useState(null);
  const [certType, setCertType] = useState('propio');
  const [currentCertDialog, setCurrentCertDialog] = useState(false);
  const [horasOnline, setHorasOnline] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showActaDialog, setShowActaDialog] = useState(false);
  const [actaCertType, setActaCertType] = useState('propio');
  const [sendingActa, setSendingActa] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBodyHtml, setEditBodyHtml] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [sendingSurvey, setSendingSurvey] = useState(false);
  const [surveyConfig, setSurveyConfig] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [pollingLogs, setPollingLogs] = useState(false);
  const [showCustomEmailDialog, setShowCustomEmailDialog] = useState(false);
  const [customEmailSubject, setCustomEmailSubject] = useState('');
  const [customEmailBody, setCustomEmailBody] = useState('');
  const [customEmailPreview, setCustomEmailPreview] = useState(null);
  const [sendingCustomEmail, setSendingCustomEmail] = useState(false);
  const [loadingCustomPreview, setLoadingCustomPreview] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [emailDrafts, setEmailDrafts] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [showSaveDraft, setShowSaveDraft] = useState(false);
  const [staff, setStaff] = useState([]);
  const [staffHoras, setStaffHoras] = useState({});
  const [staffCertType, setStaffCertType] = useState('propio');
  const [staffBusy, setStaffBusy] = useState(null);

  useEffect(() => {
    fetchData();
  }, [courseId]);

  // Polling para logs de email cuando se están enviando
  useEffect(() => {
    if (!pollingLogs) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/courses/${courseId}/email-logs`);
        setEmailLogs(res.data);
        // Si ya hay logs con resultado, parar polling
        if (res.data.length > 0) {
          const latest = res.data[0];
          if (latest.sent > 0 || latest.failed > 0) {
            setPollingLogs(false);
            if (latest.failed === 0) {
              toast.success(`${latest.sent} email(s) enviado(s) correctamente`);
            } else {
              toast.error(`${latest.sent} enviados, ${latest.failed} fallidos`);
            }
          }
        }
      } catch (e) { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [pollingLogs, courseId]);

  const fetchData = async () => {
    try {
      const [courseRes, enrollmentsRes, studentsRes, logsRes, staffRes] = await Promise.all([
        axios.get(`${API_URL}/courses/${courseId}`),
        axios.get(`${API_URL}/courses/${courseId}/enrollments`),
        axios.get(`${API_URL}/students`),
        axios.get(`${API_URL}/courses/${courseId}/email-logs`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/courses/${courseId}/staff`).catch(() => ({ data: { staff: [], course_hours: 0 } }))
      ]);
      setCourse(courseRes.data);
      setEnrollments(enrollmentsRes.data);
      setEmailLogs(logsRes.data);
      setAllStudents(studentsRes.data);
      const staffList = staffRes.data?.staff || [];
      setStaff(staffList);
      const defaultHoras = staffRes.data?.course_hours || courseRes.data?.horas || '';
      setStaffHoras((prev) => {
        const next = { ...prev };
        staffList.forEach((p) => { if (next[p.person_id] === undefined) next[p.person_id] = defaultHoras; });
        return next;
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!selectedStudent) return;
    
    try {
      await axios.post(`${API_URL}/courses/${courseId}/enrollments?student_id=${selectedStudent}`);
      toast.success('Alumno inscrito correctamente');
      setShowEnrollDialog(false);
      setSelectedStudent('');
      setSearchQuery('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al inscribir el alumno');
    }
  };

  const handleUnenroll = async (studentId) => {
    if (!window.confirm('¿Quieres dar de baja a este alumno del curso?')) return;
    
    try {
      await axios.delete(`${API_URL}/courses/${courseId}/enrollments/${studentId}`);
      toast.success('Alumno dado de baja correctamente');
      fetchData();
    } catch (error) {
      toast.error('Error al dar de baja al alumno');
    }
  };

  const handleGenerateCertificate = async () => {
    if (!selectedStudentForCert) return;
    
    try {
      // Construir URL con parámetros
      let url = `${API_URL}/courses/${courseId}/certificate/${selectedStudentForCert.id}?certificate_type=${certType}`;
      if (horasOnline && parseFloat(horasOnline) > 0) {
        url += `&horas_online=${horasOnline}`;
      }
      
      const downloadRes = await axios.get(url, { responseType: 'blob' });
      
      const blobUrl = window.URL.createObjectURL(new Blob([downloadRes.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `certificado_${selectedStudentForCert.apellidos}_${selectedStudentForCert.nombre}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Certificado generado correctamente');
      setCurrentCertDialog(false);
      setSelectedStudentForCert(null);
      setHorasOnline('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar el certificado');
    }
  };

  const downloadStaffCert = async (person) => {
    try {
      const horas = staffHoras[person.person_id];
      let url = `${API_URL}/courses/${courseId}/staff-certificate/${person.person_id}?role=${person.role}&certificate_type=${staffCertType}`;
      if (horas && parseFloat(horas) > 0) url += `&horas=${horas}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `certificado_docencia_${person.apellidos}_${person.nombre}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Certificado generado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar el certificado');
    }
  };

  const emailStaffCert = async (person) => {
    if (!person.email) { toast.error('Esta persona no tiene email registrado'); return; }
    setStaffBusy(person.person_id);
    try {
      const horas = staffHoras[person.person_id];
      let url = `${API_URL}/courses/${courseId}/staff-certificate/${person.person_id}/email?role=${person.role}&certificate_type=${staffCertType}`;
      if (horas && parseFloat(horas) > 0) url += `&horas=${horas}`;
      const res = await axios.post(url);
      toast.success(res.data?.message || 'Certificado enviado por email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el certificado');
    } finally {
      setStaffBusy(null);
    }
  };

  const handleUpdateStatus = async (studentId, newStatus) => {
    setUpdatingStatus(studentId);
    try {
      await axios.put(`${API_URL}/courses/${courseId}/enrollments/${studentId}/status?estado=${encodeURIComponent(newStatus)}`);
      
      // Actualizar estado local
      setEnrollments(prev => prev.map(e => 
        e.student?.id === studentId ? { ...e, estado: newStatus } : e
      ));
      toast.success('Estado actualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar el estado');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleGenerateActa = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/courses/${courseId}/acta`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `acta_${course?.numero_registro || courseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Acta generada correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar el acta');
    }
  };

  const handleDownloadAttendance = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/courses/${courseId}/attendance/pdf`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `asistencia_${courseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Hoja de asistencia descargada');
    } catch (error) {
      toast.error('Error al descargar la hoja de asistencia');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/courses/${courseId}/export-excel`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `alumnos_${course?.numero_registro || courseId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel exportado correctamente');
    } catch (error) {
      toast.error('Error al exportar el Excel');
    }
  };

  const handleCloseCourse = async () => {
    try {
      await axios.post(`${API_URL}/courses/${courseId}/close`);
      toast.success('Curso cerrado correctamente');
      setShowCloseDialog(false);
      fetchData(); // Recargar datos
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar el curso');
    }
  };

  const handleReopenCourse = async () => {
    try {
      await axios.post(`${API_URL}/courses/${courseId}/reopen`);
      toast.success('Curso reabierto correctamente');
      fetchData(); // Recargar datos
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reabrir el curso');
    }
  };

  const handlePreviewCertificateEmail = async () => {
    setLoadingPreview(true);
    try {
      const response = await axios.post(
        `${API_URL}/courses/${courseId}/preview-certificate-email?certificate_type=${actaCertType}`
      );
      setPreviewData(response.data);
      setEditSubject(response.data.subject);
      setEditBodyHtml(response.data.body_html);
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar previsualización');
    } finally {
      setLoadingPreview(false);
    }
  };

  const buildPreviewHtml = (bodyHtml, wrapper) => {
    if (!wrapper) return '';
    return `<!DOCTYPE html><html><head><style>
      body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
      .container { max-width: 600px; margin: 0 auto; padding: 20px; }
      .header { background-color: ${wrapper.header_color}; color: white; padding: 20px; text-align: center; }
      .content { padding: 20px; background-color: #f9f9f9; }
      .button { display: inline-block; padding: 12px 24px; background-color: ${wrapper.button_color}; color: white; text-decoration: none; border-radius: 5px; margin: 20px 0; }
      .footer { padding: 20px; text-align: center; font-size: 12px; color: #666; }
    </style></head><body><div class="container">
      <div class="header"><h1>${wrapper.header_title}</h1></div>
      <div class="content">${bodyHtml}</div>
      <div class="footer"><p>${wrapper.footer_text}</p></div>
    </div></body></html>`;
  };

  const handleDownloadPreviewPdf = () => {
    if (!previewData?.pdf_base64) return;
    const byteChars = atob(previewData.pdf_base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', previewData.pdf_filename || 'certificado_preview.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const fetchEmailDrafts = async () => {
    try {
      const res = await axios.get(`${API_URL}/courses/email-drafts`);
      setEmailDrafts(res.data);
    } catch (err) { /* silent */ }
  };

  const handleSaveDraft = async () => {
    if (!draftName.trim()) { toast.error('Escribe un nombre para la plantilla'); return; }
    setSavingDraft(true);
    try {
      await axios.post(`${API_URL}/courses/email-drafts`, {
        name: draftName, subject: customEmailSubject, body_html: customEmailBody
      });
      toast.success('Plantilla guardada');
      setShowSaveDraft(false);
      setDraftName('');
      fetchEmailDrafts();
    } catch (err) { toast.error('Error al guardar'); }
    finally { setSavingDraft(false); }
  };

  const handleLoadDraft = (draft) => {
    setCustomEmailSubject(draft.subject);
    setCustomEmailBody(draft.body_html);
    toast.success(`Plantilla "${draft.name}" cargada`);
  };

  const handleDeleteDraft = async (draftId) => {
    try {
      await axios.delete(`${API_URL}/courses/email-drafts/${draftId}`);
      setEmailDrafts(prev => prev.filter(d => d.id !== draftId));
      toast.success('Plantilla eliminada');
    } catch (err) { toast.error('Error al eliminar'); }
  };



  const isEmailBodyEmpty = !customEmailBody || customEmailBody === '<p><br></p>' || customEmailBody.replace(/<[^>]*>/g, '').trim() === '';

  const handlePreviewCustomEmail = async () => {
    if (!customEmailSubject.trim() || isEmailBodyEmpty) {
      toast.error('Escribe un asunto y un mensaje');
      return;
    }
    setLoadingCustomPreview(true);
    try {
      const response = await axios.post(
        `${API_URL}/courses/${courseId}/preview-custom-email`,
        { subject: customEmailSubject, body_html: customEmailBody }
      );
      setCustomEmailPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar previsualización');
    } finally {
      setLoadingCustomPreview(false);
    }
  };

  const handleSendCustomEmail = async () => {
    setSendingCustomEmail(true);
    try {
      const formData = new FormData();
      formData.append('subject', customEmailSubject);
      formData.append('body_html', customEmailBody);
      formData.append('student_ids', '');
      emailAttachments.forEach(file => formData.append('files', file));

      const response = await axios.post(
        `${API_URL}/courses/${courseId}/send-custom-email`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`${response.data.recipients_count} email(s) programados${response.data.attachments_count ? ` con ${response.data.attachments_count} adjunto(s)` : ''}`);
      setShowCustomEmailDialog(false);
      setCustomEmailSubject('');
      setCustomEmailBody('');
      setCustomEmailPreview(null);
      setEmailAttachments([]);
      setPollingLogs(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar emails');
    } finally {
      setSendingCustomEmail(false);
    }
  };


  const handleGenerateActaWithCertificates = async () => {
    setSendingActa(true);
    try {
      // Primero generar el acta PDF y descargarlo
      const actaResponse = await axios.get(
        `${API_URL}/courses/${courseId}/acta`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([actaResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `acta_${course?.numero_registro || courseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Luego enviar los certificados (con personalizaciones si se editaron)
      const params = new URLSearchParams({ certificate_type: actaCertType });
      if (isEditing) {
        params.append('custom_subject', editSubject);
        params.append('custom_body_html', editBodyHtml);
      }
      const certResponse = await axios.post(
        `${API_URL}/courses/${courseId}/acta-with-certificates?${params.toString()}`
      );
      
      toast.success(
        `Acta generada. ${certResponse.data.emails_programados} certificados programados para envío.`,
        { duration: 5000 }
      );
      
      // Activar polling para confirmar envío
      setPollingLogs(true);
      setShowActaDialog(false);
      setPreviewData(null);
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar acta y enviar certificados');
    } finally {
      setSendingActa(false);
    }
  };

  const enrolledStudentIds = enrollments.filter(e => e.student).map(e => e.student.id);
  const availableStudents = allStudents.filter(s => !enrolledStudentIds.includes(s.id));
  const isClosed = course?.estado_curso === 'cerrado';
  const aptosCount = enrollments.filter(e => e.estado === 'APTO').length;

  const handleSendSurvey = async () => {
    setSendingSurvey(true);
    try {
      const res = await axios.post(`${API_URL}/courses/${courseId}/send-survey`);
      toast.success(`Encuesta programada para ${res.data.emails_programados} alumnos. Se enviarán en unos minutos.`, { duration: 5000 });
      setSurveyConfig({ sent: true, ...res.data });
      setPollingLogs(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar la encuesta');
    } finally {
      setSendingSurvey(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-slate-500 mb-4">Curso no encontrado</p>
          <Button onClick={() => navigate('/courses')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Cursos
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="course-detail-container">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/courses')}
            data-testid="course-detail-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>

        {/* Banner de curso cerrado */}
        {isClosed && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Este curso está cerrado</p>
                <p className="text-sm text-amber-600">No se permiten nuevas inscripciones ni modificaciones</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleReopenCourse}
              className="border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white"
            >
              <Unlock className="w-4 h-4 mr-2" />
              Reabrir Curso
            </Button>
          </div>
        )}

        {/* Course Info */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-manrope font-bold text-3xl text-slate-900" data-testid="course-detail-title">
                  {course.nombre}
                </h1>
                {isClosed && (
                  <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                    CERRADO
                  </span>
                )}
              </div>
              {course.titulo && (
                <h2 className="font-manrope font-semibold text-xl text-slate-700 mb-2">{course.titulo}</h2>
              )}
            </div>
            {course.numero_registro && (
              <div className="bg-slate-50 px-4 py-2 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Nº Registro</p>
                <p className="font-mono font-bold text-[#004E89]">{course.numero_registro}</p>
              </div>
            )}
          </div>
          {course.descripcion && (
            <p className="font-inter text-slate-600 mb-6">{course.descripcion}</p>
          )}
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <p className="text-sm text-slate-500 mb-1">Duración</p>
              <p className="font-medium text-slate-900">{course.horas} horas</p>
            </div>
            {course.numero_sesiones && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Número de Sesiones</p>
                <p className="font-medium text-slate-900">{course.numero_sesiones}</p>
              </div>
            )}
            {course.instructor && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Instructor</p>
                <p className="font-medium text-slate-900">{course.instructor}</p>
              </div>
            )}
            {course.director_formacion && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Director de Formación</p>
                <p className="font-medium text-slate-900">
                  {course.director_formacion.nombre} {course.director_formacion.apellidos}
                </p>
              </div>
            )}
          </div>

          {(course.cliente || course.lugar_imparticion || course.localidad || course.provincia) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pt-4 border-t border-slate-200">
              {course.cliente && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Cliente</p>
                  <p className="font-medium text-slate-900">{course.cliente}</p>
                </div>
              )}
              {course.lugar_imparticion && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Lugar de Impartición</p>
                  <p className="font-medium text-slate-900">{course.lugar_imparticion}</p>
                </div>
              )}
              {course.localidad && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Localidad</p>
                  <p className="font-medium text-slate-900">{course.localidad}</p>
                </div>
              )}
              {course.provincia && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Provincia</p>
                  <p className="font-medium text-slate-900">{course.provincia}</p>
                </div>
              )}
            </div>
          )}

          {/* Enlace de Videoconferencia */}
          {course.meet_link && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">Sesión de Videoconferencia</p>
                  <a 
                    href={course.meet_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
                  >
                    {course.meet_link}
                  </a>
                </div>
                <a 
                  href={course.meet_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Unirse a la sesión
                </a>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {course.fecha_inicio && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Fecha Inicio</p>
                <p className="font-medium text-slate-900">{course.fecha_inicio}</p>
              </div>
            )}
            {course.fecha_fin && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Fecha Fin</p>
                <p className="font-medium text-slate-900">{course.fecha_fin}</p>
              </div>
            )}
            {course.hora_inicio && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Hora Inicio</p>
                <p className="font-medium text-slate-900">{course.hora_inicio}</p>
              </div>
            )}
            {course.hora_fin && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Hora Fin</p>
                <p className="font-medium text-slate-900">{course.hora_fin}</p>
              </div>
            )}
          </div>

          {course.objetivos_generales && (
            <div className="mb-4">
              <p className="text-sm font-medium text-slate-500 mb-2">Objetivos Generales</p>
              <p className="font-inter text-slate-700 whitespace-pre-line">{course.objetivos_generales}</p>
            </div>
          )}

          {course.objetivos_especificos && (
            <div className="mb-4">
              <p className="text-sm font-medium text-slate-500 mb-2">Objetivos Específicos</p>
              <p className="font-inter text-slate-700 whitespace-pre-line">{course.objetivos_especificos}</p>
            </div>
          )}

          {course.temario && (
            <div>
              <p className="text-sm font-medium text-slate-500 mb-2">Temario</p>
              <p className="font-inter text-slate-700 whitespace-pre-line">{course.temario}</p>
            </div>
          )}

          {/* Blended Learning Requisito */}
          {course.online_course && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <h3 className="font-semibold text-blue-800 mb-1">Formación Mixta (Blended Learning)</h3>
                  <p className="text-sm text-blue-700 mb-2">
                    Este curso presencial requiere completar primero el siguiente curso online:
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-blue-900">{course.online_course.nombre}</span>
                    <Button 
                      variant="link" 
                      className="text-blue-600 p-0 h-auto"
                      onClick={() => navigate(`/online-courses/${course.online_course.id}`)}
                    >
                      Ver curso →
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-4">
          {/* Botón inscribir - solo si curso abierto */}
          {!isClosed && (
            <Dialog open={showEnrollDialog} onOpenChange={(open) => { setShowEnrollDialog(open); if (!open) { setSearchQuery(''); setSelectedStudent(''); } }}>
              <DialogTrigger asChild>
                <Button className="bg-[#004E89] hover:bg-[#003B66]" data-testid="course-detail-enroll-button">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Inscribir Alumno
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Inscribir Alumno al Curso</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Buscar alumno</Label>
                    <div className="relative mt-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Nombre, apellidos o DNI..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                        data-testid="enroll-student-search"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="border rounded-md max-h-60 overflow-y-auto" data-testid="enroll-student-list">
                    {(() => {
                      const q = searchQuery.toLowerCase().trim();
                      const filtered = q
                        ? availableStudents.filter(s =>
                            `${s.nombre} ${s.apellidos} ${s.dni || ''}`.toLowerCase().includes(q)
                          )
                        : availableStudents;
                      if (filtered.length === 0) {
                        return (
                          <p className="text-sm text-slate-500 text-center py-6">
                            {q ? 'Sin resultados' : 'No hay alumnos disponibles'}
                          </p>
                        );
                      }
                      return filtered.map(student => {
                        const isSelected = selectedStudent === student.id;
                        return (
                          <button
                            key={student.id}
                            type="button"
                            onClick={() => setSelectedStudent(isSelected ? '' : student.id)}
                            className={`w-full text-left px-4 py-3 flex items-center justify-between border-b last:border-b-0 transition-colors ${
                              isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                            }`}
                            data-testid={`enroll-option-${student.id}`}
                          >
                            <div>
                              <p className={`text-sm font-medium ${isSelected ? 'text-[#004E89]' : 'text-slate-900'}`}>
                                {student.nombre} {student.apellidos}
                              </p>
                              <p className="text-xs text-slate-500">{student.dni || 'Sin DNI'} {student.email ? `· ${student.email}` : ''}</p>
                            </div>
                            {isSelected && <Check className="w-5 h-5 text-[#004E89] flex-shrink-0" />}
                          </button>
                        );
                      });
                    })()}
                  </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowEnrollDialog(false)} data-testid="enroll-cancel">
                    Cancelar
                  </Button>
                  <Button onClick={handleEnroll} disabled={!selectedStudent} className="bg-[#004E89] hover:bg-[#003B66]" data-testid="enroll-submit">
                    Inscribir
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          )}

          <Button onClick={handleDownloadAttendance} variant="outline" data-testid="course-detail-attendance-button">
            <Download className="w-4 h-4 mr-2" />
            Hoja de Asistencia
          </Button>

          <Button onClick={handleGenerateActa} variant="outline" className="border-[#004E89] text-[#004E89] hover:bg-[#004E89] hover:text-white" data-testid="course-detail-acta-button">
            <FileText className="w-4 h-4 mr-2" />
            Generar Acta
          </Button>

          {/* Botón Enviar Email Personalizado */}
          <Button 
            onClick={() => { setShowCustomEmailDialog(true); setCustomEmailPreview(null); fetchEmailDrafts(); }}
            variant="outline" 
            className="border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white"
            data-testid="course-detail-send-email-button"
          >
            <Send className="w-4 h-4 mr-2" />
            Enviar Email
          </Button>

          {/* Botón Acta + Enviar Certificados */}
          <Dialog open={showActaDialog} onOpenChange={(open) => { setShowActaDialog(open); if (!open) setPreviewData(null); }}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white"
                data-testid="course-detail-acta-certificates-button"
              >
                <Send className="w-4 h-4 mr-2" />
                Acta + Enviar Certificados
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Generar Acta y Enviar Certificados</DialogTitle>
                <DialogDescription>
                  {previewData 
                    ? 'Previsualización del email que recibirán los alumnos APTO.'
                    : 'Selecciona el tipo de certificado y previsualiza antes de enviar.'}
                </DialogDescription>
              </DialogHeader>

              {!previewData ? (
                <>
                  <div className="space-y-4 py-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Alumnos APTO:</strong> {aptosCount} de {enrollments.length} inscritos
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Se enviará un email con el certificado PDF adjunto a cada alumno APTO
                      </p>
                    </div>
                    <div>
                      <Label>Tipo de Certificado</Label>
                      <Select value={actaCertType} onValueChange={setActaCertType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="propio">Certificado Propio</SelectItem>
                          <SelectItem value="desa_cv">DESA Comunidad Valenciana</SelectItem>
                          <SelectItem value="cfc">Certificado CFC</SelectItem>
                          <SelectItem value="manual">Certificado Manual</SelectItem>
                          <SelectItem value="proteccion_civil">Protección Civil</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowActaDialog(false)}>
                      Cancelar
                    </Button>
                    <Button
                      onClick={handlePreviewCertificateEmail}
                      disabled={loadingPreview || aptosCount === 0}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="preview-certificate-email-button"
                    >
                      {loadingPreview ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Generando...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Previsualizar Email
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <div className="space-y-3 py-2">
                    {/* Asunto editable */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs font-medium text-slate-500">Asunto del email</Label>
                        {!isEditing && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700"
                            onClick={() => setIsEditing(true)}
                            data-testid="edit-email-button"
                          >
                            <Pencil className="w-3 h-3 mr-1" />
                            Personalizar
                          </Button>
                        )}
                      </div>
                      {isEditing ? (
                        <Input
                          value={editSubject}
                          onChange={(e) => setEditSubject(e.target.value)}
                          className="text-sm"
                          data-testid="edit-subject-input"
                        />
                      ) : (
                        <div className="bg-amber-50 border border-amber-200 px-3 py-2 rounded text-sm text-amber-800">
                          {editSubject}
                        </div>
                      )}
                    </div>

                    {/* Cuerpo editable */}
                    {isEditing && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-xs font-medium text-slate-500">Contenido del email (HTML)</Label>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs text-slate-500 hover:text-slate-700"
                            onClick={() => {
                              setEditSubject(previewData.subject);
                              setEditBodyHtml(previewData.body_html);
                              setIsEditing(false);
                            }}
                            data-testid="reset-email-button"
                          >
                            Restaurar original
                          </Button>
                        </div>
                        <textarea
                          value={editBodyHtml}
                          onChange={(e) => setEditBodyHtml(e.target.value)}
                          className="w-full h-[140px] text-xs font-mono border rounded-md p-2 bg-slate-50 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid="edit-body-textarea"
                        />
                      </div>
                    )}

                    {/* Vista previa del email */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 border-b flex items-center justify-between">
                        <span>Vista previa del email</span>
                        <span className="text-slate-400 text-[10px]">
                          Ejemplo para: {previewData.sample_student}
                        </span>
                      </div>
                      <div
                        className="p-0 bg-white"
                        data-testid="email-preview-content"
                      >
                        <iframe
                          title="Email preview"
                          srcDoc={isEditing ? buildPreviewHtml(editBodyHtml, previewData.wrapper) : previewData.html_content}
                          className="w-full h-[220px] border-0"
                          sandbox=""
                        />
                      </div>
                    </div>

                    {/* PDF adjunto */}
                    <div className="flex items-center justify-between bg-slate-50 p-2.5 rounded-lg border">
                      <div>
                        <p className="text-sm font-medium text-slate-700">Certificado PDF adjunto</p>
                        <p className="text-xs text-slate-500">{previewData.pdf_filename} ({previewData.pdf_size_kb} KB)</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleDownloadPreviewPdf}
                        data-testid="download-preview-pdf-button"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Descargar
                      </Button>
                    </div>

                    {isEditing && (
                      <p className="text-[11px] text-amber-600 bg-amber-50 px-3 py-1.5 rounded">
                        Usa <code className="bg-amber-100 px-1 rounded">{'{nombre_alumno}'}</code> en el texto para insertar el nombre de cada alumno automáticamente.
                      </p>
                    )}
                  </div>

                  <DialogFooter className="flex gap-2">
                    <Button variant="outline" onClick={() => { setPreviewData(null); setIsEditing(false); }}>
                      Volver
                    </Button>
                    <Button
                      onClick={handleGenerateActaWithCertificates}
                      disabled={sendingActa}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="confirm-send-certificates-button"
                    >
                      {sendingActa ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Confirmar y Enviar ({aptosCount})
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>

          <Button onClick={handleExportExcel} variant="outline" className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white" data-testid="course-detail-excel-button">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Exportar Excel
          </Button>

          {/* Botón Enviar Encuesta */}
          <Button 
            onClick={handleSendSurvey} 
            disabled={sendingSurvey}
            variant="outline" 
            className="border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white"
            data-testid="course-detail-send-survey-button"
          >
            {sendingSurvey ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                Enviando...
              </>
            ) : (
              <>
                <ClipboardList className="w-4 h-4 mr-2" />
                Enviar Encuesta
              </>
            )}
          </Button>

          {/* Botón Ver Resultados Encuesta */}
          <Button 
            onClick={() => navigate(`/courses/${courseId}/survey-results`)} 
            variant="outline" 
            className="border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white"
            data-testid="course-detail-survey-results-button"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Resultados Encuesta
          </Button>

          {/* Botón Cerrar Curso - solo si curso abierto */}
          {!isClosed && (
            <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                  data-testid="course-detail-close-button"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Cerrar Curso
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>¿Cerrar este curso?</DialogTitle>
                  <DialogDescription>
                    Al cerrar el curso:
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2">
                      <span className="text-red-500">✕</span> No se podrán inscribir nuevos alumnos
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-red-500">✕</span> No se podrá modificar la información del curso
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> Se podrán cambiar estados de alumnos
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> Se podrán generar certificados y actas
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-500">↻</span> El curso se puede reabrir más tarde
                    </li>
                  </ul>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleCloseCourse}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Cerrar Curso
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Estado de envíos de email */}
        {pollingLogs && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3" data-testid="email-sending-status">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <p className="text-sm text-blue-800 font-medium">Enviando emails en segundo plano... Se actualizará automáticamente.</p>
          </div>
        )}
        {emailLogs.length > 0 && !pollingLogs && (
          <div className={`rounded-lg p-4 border ${emailLogs[0].failed > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`} data-testid="email-log-result">
            <div className="flex items-center justify-between">
              <p className={`text-sm font-medium ${emailLogs[0].failed > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                Último envío ({emailLogs[0].type === 'certificates' ? 'Certificados' : emailLogs[0].type === 'custom' ? 'Email personalizado' : 'Encuestas'}):
                {' '}{emailLogs[0].sent} enviado(s){emailLogs[0].failed > 0 ? `, ${emailLogs[0].failed} fallido(s)` : ''} de {emailLogs[0].total} total
                {' '} — {new Date(emailLogs[0].timestamp).toLocaleString('es-ES')}
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API_URL}/courses/test-email`);
                    if (res.data.success) {
                      toast.success(`OK: ${res.data.message}\n${res.data.config}`, { duration: 15000 });
                    } else {
                      toast.error(`ERROR: ${res.data.error}\n\nConfig: ${res.data.config}`, { duration: 30000 });
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Error al enviar test');
                  }
                }}
                data-testid="test-email-button"
              >
                Test Email
              </Button>
            </div>
            {emailLogs[0].errors?.length > 0 && (
              <ul className="mt-2 text-xs text-amber-700">
                {emailLogs[0].errors.map((err, i) => (
                  <li key={i}>{err.email}: {err.error}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Preinscripciones */}
        <CoursePreinscriptions courseId={courseId} courseType="presencial" onApproved={fetchData} />

        {/* Certificados de Docencia (docentes y director) */}
        {staff.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="staff-certificates-section">
            <div className="px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#004E89]" />
                <h2 className="font-manrope font-semibold text-xl text-slate-900">Certificados de Docencia</h2>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-500 whitespace-nowrap">Tipo</Label>
                <Select value={staffCertType} onValueChange={setStaffCertType}>
                  <SelectTrigger className="w-48" data-testid="staff-cert-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="propio">Certificado Propio</SelectItem>
                    <SelectItem value="desa_cv">DESA Comunidad Valenciana</SelectItem>
                    <SelectItem value="cfc">Certificado CFC</SelectItem>
                    <SelectItem value="manual">Certificado Manual</SelectItem>
                    <SelectItem value="proteccion_civil">Protección Civil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="px-6 pt-3 text-sm text-slate-500">
              Expide certificados de horas impartidas para el director y los docentes, con el mismo formato que los de los alumnos y código de verificación (CSV + QR).
            </p>
            <div className="divide-y divide-slate-100">
              {staff.map((p) => (
                <div key={p.person_id} className="px-6 py-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="staff-cert-row">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900 truncate">{p.nombre} {p.apellidos}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.role === 'director' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'}`}>
                        {p.role === 'director' ? 'Director/a' : 'Docente'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 truncate">{p.email || 'Sin email'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-500 whitespace-nowrap">Horas impartidas</Label>
                    <Input
                      type="number" min="0" className="w-24"
                      value={staffHoras[p.person_id] ?? ''}
                      onChange={(e) => setStaffHoras((h) => ({ ...h, [p.person_id]: e.target.value }))}
                      data-testid="staff-horas-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => downloadStaffCert(p)} className="border-[#004E89] text-[#004E89] hover:bg-[#004E89] hover:text-white" data-testid="staff-cert-download">
                      <Download className="w-4 h-4 mr-1" /> Descargar
                    </Button>
                    <Button size="sm" onClick={() => emailStaffCert(p)} disabled={staffBusy === p.person_id || !p.email} className="bg-[#004E89] hover:bg-[#003B66]" data-testid="staff-cert-email">
                      <Send className="w-4 h-4 mr-1" /> {staffBusy === p.person_id ? 'Enviando...' : 'Enviar email'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enrolled Students */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-manrope font-semibold text-xl text-slate-900">
              Alumnos Inscritos ({enrollments.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="enrollments-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Nombre y Apellidos
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    DNI/NIE
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {enrollments.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-slate-500">
                      No hay alumnos inscritos en este curso
                    </td>
                  </tr>
                ) : (
                  enrollments.filter(e => e.student).map((enrollment) => (
                    <tr key={enrollment.enrollment_id || enrollment.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900">
                          {enrollment.student?.nombre} {enrollment.student?.apellidos}
                        </p>
                        <p className="text-xs text-slate-500">{enrollment.student?.email || '-'}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">{enrollment.student?.dni || '-'}</td>
                      <td className="px-6 py-4">
                        <Select 
                          value={enrollment.estado || 'PENDIENTE'} 
                          onValueChange={(value) => handleUpdateStatus(enrollment.student?.id, value)}
                          disabled={updatingStatus === enrollment.student?.id}
                        >
                          <SelectTrigger 
                            className={`w-[180px] text-xs font-medium ${
                              ESTADOS_ALUMNO.find(e => e.value === (enrollment.estado || 'PENDIENTE'))?.color || ''
                            }`}
                            data-testid={`status-select-${enrollment.student?.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ESTADOS_ALUMNO.map((estado) => (
                              <SelectItem key={estado.value} value={estado.value}>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${estado.color}`}>
                                  {estado.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Dialog open={currentCertDialog && selectedStudentForCert?.id === enrollment.student?.id} onOpenChange={(open) => {
                            if (!open) {
                              setCurrentCertDialog(false);
                              setSelectedStudentForCert(null);
                            }
                          }}>
                            <DialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedStudentForCert(enrollment.student);
                                  setCurrentCertDialog(true);
                                }}
                                title="Generar Certificado"
                                data-testid={`generate-cert-${enrollment.student?.id}`}
                              >
                                <Award className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Generar Certificado</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <p className="font-medium text-slate-900 mb-2">
                                    Alumno: {selectedStudentForCert?.nombre} {selectedStudentForCert?.apellidos}
                                  </p>
                                  <Label>Tipo de Certificado</Label>
                                  <Select value={certType} onValueChange={setCertType}>
                                    <SelectTrigger data-testid="cert-type-select">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="propio">Certificado Propio</SelectItem>
                                      <SelectItem value="desa_cv">DESA Comunidad Valenciana</SelectItem>
                                      <SelectItem value="cfc">Certificado CFC (Formación Continuada)</SelectItem>
                                      <SelectItem value="manual">Certificado Manual</SelectItem>
                                      <SelectItem value="proteccion_civil">Protección Civil</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {certType === 'cfc' && (
                                    <p className="text-xs text-amber-600 mt-2">
                                      Asegúrate de haber configurado los créditos CFC en el curso
                                    </p>
                                  )}
                                </div>
                                
                                {/* Campo para horas de formación online */}
                                {course?.modalidad === 'mixta' && (
                                  <div>
                                    <Label htmlFor="horas-online">Horas de Formación Online (opcional)</Label>
                                    <Input
                                      id="horas-online"
                                      type="number"
                                      min="0"
                                      step="0.5"
                                      placeholder="Ej: 6"
                                      value={horasOnline}
                                      onChange={(e) => setHorasOnline(e.target.value)}
                                      className="mt-1"
                                      data-testid="horas-online-input"
                                    />
                                    <p className="text-xs text-slate-500 mt-1">
                                      Estas horas se sumarán a las {course?.horas || 0} horas presenciales del curso
                                    </p>
                                    {horasOnline && parseFloat(horasOnline) > 0 && (
                                      <p className="text-xs text-green-600 mt-1 font-medium">
                                        Total en certificado: {(parseFloat(course?.horas || 0) + parseFloat(horasOnline)).toFixed(0)} horas
                                      </p>
                                    )}
                                  </div>
                                )}
                                
                                <div className="flex justify-end gap-2">
                                  <Button variant="outline" onClick={() => {
                                    setCurrentCertDialog(false);
                                    setSelectedStudentForCert(null);
                                    setHorasOnline('');
                                  }} data-testid="cert-cancel">
                                    Cancelar
                                  </Button>
                                  <Button onClick={handleGenerateCertificate} className="bg-[#004E89] hover:bg-[#003B66]" data-testid="cert-generate">
                                    Generar
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnenroll(enrollment.student?.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`unenroll-${enrollment.student?.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Diálogo Enviar Email Personalizado */}
      <Dialog open={showCustomEmailDialog} onOpenChange={(open) => { setShowCustomEmailDialog(open); if (!open) { setCustomEmailPreview(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enviar Email a Alumnos</DialogTitle>
            <DialogDescription>
              {customEmailPreview
                ? 'Revisa cómo se verá el email antes de enviarlo.'
                : `Redacta un email para los ${enrollments.length} alumno(s) inscrito(s) en este curso.`}
            </DialogDescription>
          </DialogHeader>

          {!customEmailPreview ? (
            <div className="space-y-4 py-2">
              <div className="bg-teal-50 border border-teal-200 p-3 rounded-lg">
                <p className="text-sm text-teal-800">
                  <strong>Destinatarios:</strong> {enrollments.filter(e => e.student?.email).length} alumno(s) con email de {enrollments.length} inscritos
                </p>
                <p className="text-xs text-teal-600 mt-1">
                  El email incluirá automáticamente el logotipo de IRTA y la política de privacidad en la firma
                </p>
              </div>

              {/* Plantillas guardadas */}
              {emailDrafts.length > 0 && (
                <div>
                  <Label className="text-xs font-medium text-slate-500 flex items-center gap-1 mb-1.5">
                    <BookOpen className="w-3 h-3" /> Plantillas guardadas
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {emailDrafts.map(draft => (
                      <div key={draft.id} className="flex items-center gap-1 bg-white border rounded-md px-2 py-1 text-xs group">
                        <button
                          onClick={() => handleLoadDraft(draft)}
                          className="text-teal-700 hover:text-teal-900 font-medium"
                          data-testid={`load-draft-${draft.id}`}
                        >
                          {draft.name}
                        </button>
                        <button
                          onClick={() => handleDeleteDraft(draft.id)}
                          className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-draft-${draft.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label>Asunto</Label>
                <Input
                  value={customEmailSubject}
                  onChange={(e) => setCustomEmailSubject(e.target.value)}
                  placeholder="Ej: Información sobre tu próxima formación"
                  data-testid="custom-email-subject-input"
                />
              </div>

              <div>
                <Label>Mensaje</Label>
                <RichTextEditor
                  value={customEmailBody}
                  onChange={setCustomEmailBody}
                  placeholder="Estimado/a alumno, nos ponemos en contacto..."
                />
                <p className="text-[11px] text-teal-600 mt-1">
                  Usa <code className="bg-teal-100 px-1 rounded">{'{nombre_alumno}'}</code> para personalizar con el nombre de cada alumno.
                </p>
              </div>

              {/* Archivos adjuntos */}
              <div>
                <Label className="flex items-center gap-1">Archivos adjuntos</Label>
                <div className="flex items-center gap-2 mt-1">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-dashed border-slate-300 hover:border-teal-500 hover:bg-teal-50 text-sm text-slate-600 transition-colors">
                    <Paperclip className="w-4 h-4" />
                    <span>Adjuntar archivos</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => setEmailAttachments(prev => [...prev, ...Array.from(e.target.files)])}
                      data-testid="email-attachments-input"
                    />
                  </label>
                  {emailAttachments.length > 0 && (
                    <span className="text-xs text-slate-500">{emailAttachments.length} archivo(s)</span>
                  )}
                </div>
                {emailAttachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {emailAttachments.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-50 px-2 py-1 rounded text-xs">
                        <span className="text-slate-700 truncate max-w-[300px]">{file.name} <span className="text-slate-400">({(file.size / 1024).toFixed(0)} KB)</span></span>
                        <button
                          onClick={() => setEmailAttachments(prev => prev.filter((_, i) => i !== idx))}
                          className="text-slate-400 hover:text-red-500"
                          data-testid={`remove-attachment-${idx}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Guardar como plantilla */}
              {showSaveDraft ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    placeholder="Nombre de la plantilla..."
                    className="text-sm flex-1"
                    data-testid="draft-name-input"
                  />
                  <Button size="sm" onClick={handleSaveDraft} disabled={savingDraft} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-save-draft-button">
                    {savingDraft ? '...' : 'Guardar'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowSaveDraft(false)}>
                    Cancelar
                  </Button>
                </div>
              ) : (
                customEmailSubject.trim() && !isEmailBodyEmpty && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs text-slate-500 hover:text-teal-700"
                    onClick={() => setShowSaveDraft(true)}
                    data-testid="save-as-draft-button"
                  >
                    <Save className="w-3 h-3 mr-1" />
                    Guardar como plantilla
                  </Button>
                )
              )}

              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setShowCustomEmailDialog(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handlePreviewCustomEmail}
                  disabled={loadingCustomPreview || !customEmailSubject.trim() || isEmailBodyEmpty}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="preview-custom-email-button"
                >
                  {loadingCustomPreview ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Generando...</>
                  ) : (
                    <><FileText className="w-4 h-4 mr-2" />Previsualizar</>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="bg-amber-50 border border-amber-200 px-3 py-2 rounded text-sm text-amber-800">
                <strong>Asunto:</strong> {customEmailPreview.subject}
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 border-b flex justify-between">
                  <span>Vista previa del email</span>
                  <span className="text-slate-400 text-[10px]">Ejemplo para: {customEmailPreview.sample_student}</span>
                </div>
                <iframe
                  title="Custom email preview"
                  srcDoc={customEmailPreview.html_content}
                  className="w-full h-[350px] border-0"
                  sandbox=""
                  data-testid="custom-email-preview-iframe"
                />
              </div>

              {emailAttachments.length > 0 && (
                <div className="bg-slate-50 border rounded-lg p-2.5">
                  <p className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> {emailAttachments.length} archivo(s) adjunto(s)
                  </p>
                  <div className="space-y-1">
                    {emailAttachments.map((file, idx) => (
                      <div key={idx} className="text-xs text-slate-500">{file.name} ({(file.size / 1024).toFixed(0)} KB)</div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setCustomEmailPreview(null)}>
                  Editar
                </Button>
                <Button
                  onClick={handleSendCustomEmail}
                  disabled={sendingCustomEmail}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="confirm-send-custom-email-button"
                >
                  {sendingCustomEmail ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Enviando...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />Enviar a {enrollments.filter(e => e.student?.email).length} alumno(s)</>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CourseDetail;